import { UseDisclosureReturn, VStack } from "@chakra-ui/react";
import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import { isSylaApiErrorCode } from "@syla/shared/errors/SylaApiError";
import React from "react";
import { useForm } from "react-hook-form";
import { parseErrorToUserMessage } from "../../../../api/errors/parsers/parseErrorToUserMessage";
import { useMutationChangePassword } from "../../../../store/useMutationChangePassword";
import {
  PasswordRequirements,
  validatePasswordInput,
} from "../../../atoms/PasswordRequirements";
import { InputField } from "../../../forms/InputField";
import { FormModal } from "../../../modals/FormModal";

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export function ChangePasswordModal({
  disclosure,
}: {
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const form = useForm<ChangePasswordFormValues>({
    mode: "onTouched",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const mutationChangePassword = useMutationChangePassword();

  const onSubmit = async (data: ChangePasswordFormValues) => {
    try {
      await mutationChangePassword.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    } catch (error) {
      if (isSylaApiErrorCode(error, ProblemCode.IncorrectPassword)) {
        form.setError("currentPassword", {
          type: "server",
          message: parseErrorToUserMessage(error).description,
        });

        return false;
      }

      if (isSylaApiErrorCode(error, ProblemCode.SamePassword)) {
        form.setError("newPassword", {
          type: "server",
          message: parseErrorToUserMessage(error).description,
        });

        return false;
      }

      throw error;
    }
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title="Change password"
      submitButtonText="Update password"
      successTitle="Password updated"
      errorTitle="Password change failed"
      onSubmit={onSubmit}
    >
      <VStack align="stretch" spacing={4}>
        <InputField
          form={form}
          name="currentPassword"
          label="Current password"
          type="password"
          autoComplete="current-password"
          isRequired
          autoFocus
        />

        <InputField
          form={form}
          name="newPassword"
          label="New password"
          type="password"
          isRequired
          autoComplete="new-password"
          helperText={<PasswordRequirements />}
          rules={{
            validate: validatePasswordInput,
          }}
        />

        <InputField
          form={form}
          name="confirmPassword"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          isRequired
          rules={{
            validate: (password?: string) =>
              password === form.getValues("newPassword") ?
                undefined
              : "Password does not match new password.",
          }}
        />
      </VStack>
    </FormModal>
  );
}
